import TextButton from 'LuxKit/Button/TextButton'
import DropdownSheetFooter from 'LuxKit/Dropdown/Sheet/DropdownSheetFooter'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { memo, useCallback, useContext } from 'react'
import OfferCalenderDatePickerFooterPriceDetails from './OfferCalenderDatePickerFooterPriceDetails'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from 'components/OfferPage/OfferBookButtons/OfferSelectPackageButton'
import { OFFER_TYPE_VILLA } from 'constants/offer'
import { useIntl } from 'react-intl'

interface Props {
  canBook?: boolean
  offer: App.Offer
  selectedDay?: App.CalendarDay
  duration: number
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView?: App.OfferPackageOptionView
  isAnyFlightsActive?: boolean
  isSelectedTargetUpgradePackage: boolean
  /** When true, user is viewing month picker and hasn't selected a specific month yet */
  isOnMonthPicker?: boolean
  showFromPrice?: boolean
  isFixedFlashLengthOfStayEnabled?: boolean
  minimumDuration?: number
  onAfterResetDates?: () => void
}

function OfferCalendarDatePickerDropdownFooter(props: Props) {
  const intl = useIntl()
  const {
    canBook,
    offer,
    selectedDay,
    duration,
    relevantPackages,
    selectedPackageView,
    isAnyFlightsActive,
    isSelectedTargetUpgradePackage,
    isOnMonthPicker,
    showFromPrice,
    isFixedFlashLengthOfStayEnabled = false,
    minimumDuration,
    onAfterResetDates,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const hasDateSelected = !!selectedDay && selectedDay.hotelAvailable && !isOnMonthPicker
  const canAddToUserCart = hasDateSelected && !isSelectedTargetUpgradePackage && canBook && offer.type !== OFFER_TYPE_VILLA
  const canShowBookNow = hasDateSelected && canBook

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch])

  const onBookNowButtonClick = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [onUserAnalyticsAction])

  const onResetDates = useCallback(() => {
    if (isFixedFlashLengthOfStayEnabled && minimumDuration !== undefined) {
      dispatch({ type: OfferPageActions.resetDatesToMinimumDuration, minimumDuration })
      onAfterResetDates?.()
      return
    }

    dispatch({ type: OfferPageActions.resetDates })
    onAfterResetDates?.()
  }, [dispatch, isFixedFlashLengthOfStayEnabled, minimumDuration, onAfterResetDates])

  return <DropdownSheetFooter
    startSlot={selectedDay && <OfferCalenderDatePickerFooterPriceDetails
      offer={offer}
      duration={duration}
      selectedDay={selectedDay}
      isAnyFlightsActive={isAnyFlightsActive}
      isFromPrice={showFromPrice || relevantPackages.length > 1}
    />}
    actionsSlot={<>
      {!!selectedDay && <TextButton kind="tertiary" size="large" onClick={onResetDates}>
        {intl.formatMessage({
          id: 'l34vjHPep6KxS9oP0jMJ',
          defaultMessage: 'Reset dates',
        })}
      </TextButton>}
      {canAddToUserCart && <>
        {isAnyFlightsActive && <OfferSelectPackageButton
          size="large"
          data-buytype="flights"
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
        {!isAnyFlightsActive && <OfferAddToCartButton
          size="large"
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
      </>}
      {canShowBookNow && !isAnyFlightsActive && offer.type === OFFER_TYPE_VILLA && <TextButton
        kind="primary"
        size="large"
        data-testid="offer-calendar-apply"
        onClick={onContinue}
      >
        {intl.formatMessage({
          id: '3TC2y07y0gyi_2PPfLg5',
          defaultMessage: 'Confirm',
        })}
      </TextButton>}
      {canShowBookNow && !isAnyFlightsActive && offer.type !== OFFER_TYPE_VILLA && <OfferBookButton
        size="large"
        data-testid="offer-calendar-apply"
        data-buytype={isAnyFlightsActive ? 'flights' : undefined}
        packageView={selectedPackageView ?? relevantPackages[0]}
        onSubmit={onBookNowButtonClick}
      >
        {intl.formatMessage({
          id: 'IQ9I9sGCwAGfNaJY3LQu',
          defaultMessage: 'Book now',
        })}
      </OfferBookButton>}
      {!canShowBookNow && !showFromPrice && <TextButton
        kind="primary"
        size="large"
        data-testid="offer-calendar-apply"
        onClick={onContinue}
      >
        {intl.formatMessage({
          id: '3xrYWj_0p3WuRTO38NnD',
          defaultMessage: 'Continue',
        })}
      </TextButton>}
    </>}
  />
}

export default memo(OfferCalendarDatePickerDropdownFooter)
